import { useState, type ReactNode } from 'react';
import { useEscapeKey } from '../../../../../hooks/useEscapeKey';
import { buildDmPrefill, type DmPrefillSource } from './dmPrefill.ts';
import { DmModalHeader, DM_LINK_TITLE_ID } from './DmModalHeader';
import { DmNewDoctorStep } from './DmNewDoctorStep';
import { DmPracticeCreatePane } from './DmPracticeCreatePane';
import { addDmPractice } from './dmPracticeList.ts';
import { DmSegmented } from './DmSegmented';
import { ExistingDoctorPane } from './ExistingDoctorPane';
import { useDmDoctorStep } from './useDmDoctorStep';
import { useDmLinkSave } from './useDmLinkSave';
import { useDmPracticeCreate } from './useDmPracticeCreate';

type DmLinkPath = 'existing' | 'new';

const PATH_OPTIONS: readonly { value: DmLinkPath; label: string }[] = [
    { value: 'existing', label: 'Existing doctor' },
    { value: 'new', label: 'New doctor' }
];

interface DmLinkModalProps {
    practiceId: string;
    prefillSource: DmPrefillSource;
    onLinked: () => void;
    onClose: () => void;
}

function DmDialogFrame({ children }: { children: ReactNode }) {
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={DM_LINK_TITLE_ID}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-8"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[560px] h-[85vh] flex flex-col overflow-hidden">
                {children}
            </div>
        </div>
    );
}

export function DmLinkModal({ practiceId, prefillSource, onLinked, onClose }: DmLinkModalProps) {
    const [prefill] = useState(() => buildDmPrefill(prefillSource));
    const [path, setPath] = useState<DmLinkPath>('existing');
    const { save, isSaving } = useDmLinkSave(practiceId, () => {
        onLinked();
        onClose();
    });
    const doctorStep = useDmDoctorStep(practiceId, prefill.doctor, save);
    const practiceCreate = useDmPracticeCreate(practiceId, prefill.practice, (practice) =>
        doctorStep.setPractices((list) => addDmPractice(list, practice))
    );
    const busy = isSaving || practiceCreate.isCreating || doctorStep.isCreating;
    useEscapeKey(onClose, !busy);

    const newDoctorPane = practiceCreate.isOpen ? (
        <DmPracticeCreatePane create={practiceCreate} />
    ) : (
        <DmNewDoctorStep
            practiceId={practiceId}
            step={doctorStep}
            isSaving={isSaving}
            onCreatePractice={practiceCreate.open}
            onCancel={onClose}
        />
    );

    return (
        <DmDialogFrame>
            <DmModalHeader onClose={onClose} disabled={busy} />
            <div className="px-6 pb-4 shrink-0">
                <DmSegmented
                    options={PATH_OPTIONS}
                    value={path}
                    onChange={setPath}
                    disabled={busy}
                />
            </div>
            {path === 'existing' ? (
                <ExistingDoctorPane
                    practiceId={practiceId}
                    initialSearch={prefill.doctorSearch}
                    save={save}
                    isSaving={isSaving}
                    onCancel={onClose}
                />
            ) : (
                newDoctorPane
            )}
        </DmDialogFrame>
    );
}
